import { Component, input, output, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentVariant } from '../types';

@Component({
  selector: 'app-toast',
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <div
        [class]="toastClasses"
        [attr.role]="'alert'"
        [attr.aria-live]="life() === 0 ? 'assertive' : 'polite'">
        <div class="flex items-start gap-3">
          @if (showIcon()) {
            <div [class]="iconClasses">
              <span class="text-2xl">{{ getEmoji() }}</span>
            </div>
          }
          <div class="flex-1">
            @if (summary()) {
              <div [class]="summaryClasses">{{ summary() }}</div>
            }
            <div [class]="messageClasses">
              <ng-content></ng-content>
            </div>
          </div>
          @if (closable()) {
            <button
              type="button"
              (click)="close()"
              class="flex-shrink-0 p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
              aria-label="Close">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          }
        </div>
      </div>
    }
  `,
  styles: []
})
export class ToastComponent implements OnDestroy {
  summary = input<string>('');
  variant = input<ComponentVariant>('info');
  life = input<number>(3000);
  position = input<'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center'>('top-right');
  closable = input<boolean>(true);
  showIcon = input<boolean>(true);

  closeEvent = output<void>();

  visible = signal(true);
  private timeoutId?: number;

  constructor() {
    if (this.life() > 0) {
      this.timeoutId = window.setTimeout(() => {
        this.close();
      }, this.life());
    }
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  close(): void {
    this.visible.set(false);
    this.closeEvent.emit();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  get toastClasses(): string {
    const classes = [
      'flex',
      'items-start',
      'gap-3',
      'p-4',
      'rounded-lg',
      'shadow-lg',
      'border-2',
      'min-w-[300px]',
      'max-w-md',
      'transition-all',
      'duration-300',
      'animate-in',
      'slide-in-from-top'
    ];

    const variantMap: Record<ComponentVariant, string> = {
      success: 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-900 dark:text-green-100',
      error: 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-900 dark:text-red-100',
      warning: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100',
      info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-100',
      primary: 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-100',
      secondary: 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100',
      danger: 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-900 dark:text-red-100'
    };
    classes.push(variantMap[this.variant()] || variantMap.info);

    return classes.join(' ');
  }

  get iconClasses(): string {
    return 'flex-shrink-0';
  }

  get summaryClasses(): string {
    return 'font-semibold mb-1';
  }

  get messageClasses(): string {
    return 'text-sm';
  }

  getEmoji(): string {
    const emojiMap: Record<ComponentVariant, string> = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️',
      primary: 'ℹ️',
      secondary: '📋',
      danger: '❌'
    };
    return emojiMap[this.variant()] || emojiMap.info;
  }

  getIconPath(): string {
    const iconMap: Record<ComponentVariant, string> = {
      success: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
      error: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
      warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
      info: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
      primary: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
      secondary: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
      danger: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
    };
    return iconMap[this.variant()] || iconMap.info;
  }
}

